import { X } from "lucide-react";
import { useEffect, useId, useRef, type ReactNode } from "react";
import { useLockBodyScroll } from "../hooks/useLockBodyScroll";

/**
 * Modal surface. "panel" is a bottom sheet on phones and a right-hand drawer on larger screens;
 * "center" is a small focused dialog (verification codes, confirmations).
 */
export function Dialog({ open, onClose, title, description, children, variant = "panel", closeLabel = "Close" }: {
  open: boolean; onClose: () => void; title: ReactNode; description?: ReactNode; children: ReactNode;
  variant?: "panel" | "center"; closeLabel?: string;
}) {
  const titleId = useId();
  const surface = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  useLockBodyScroll(open);

  useEffect(() => { closeRef.current = onClose; }, [onClose]);

  // Keyed only on `open`, so re-renders while the dialog is up (typing, timers) never move focus.
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") closeRef.current(); };
    window.addEventListener("keydown", onKey);
    if (!surface.current?.contains(document.activeElement)) surface.current?.focus();
    return () => { window.removeEventListener("keydown", onKey); previous?.focus?.(); };
  }, [open]);

  if (!open) return null;
  const placement = variant === "center"
    ? "inset-x-4 top-1/2 mx-auto max-w-sm -translate-y-1/2 rounded-2xl [animation:fade-in_.2s_ease-out]"
    : "inset-x-0 bottom-0 max-h-[88vh] rounded-t-[20px] [animation:sheet-up_.32s_cubic-bezier(.2,.8,.2,1)] sm:inset-y-0 sm:left-auto sm:right-0 sm:max-h-none sm:w-[420px] sm:rounded-none sm:[animation:drawer-in_.32s_cubic-bezier(.2,.8,.2,1)]";

  return (
    <div className="fixed inset-0 z-[60]">
      <button type="button" aria-label={closeLabel} tabIndex={-1} onClick={onClose} className="absolute inset-0 bg-cocoa/40 [animation:fade-in_.2s_ease-out]" />
      <div ref={surface} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} className={`absolute flex flex-col bg-white outline-none ${placement}`}>
        {variant === "panel" && <span className="mx-auto mt-2.5 h-1 w-10 rounded-full bg-line sm:hidden" aria-hidden="true" />}
        <div className="flex items-start justify-between gap-4 px-5 pb-3 pt-4 sm:px-6 sm:pt-6">
          <div>
            <h2 id={titleId} className="font-display text-2xl leading-tight text-cocoa">{title}</h2>
            {description && <p className="mt-1 text-sm text-muted">{description}</p>}
          </div>
          <button type="button" onClick={onClose} aria-label={closeLabel} className="-mr-2 grid size-10 shrink-0 place-items-center rounded-full text-muted transition hover:bg-frosting hover:text-cocoa"><X size={18} /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-6 sm:px-6">{children}</div>
      </div>
    </div>
  );
}
